import {
  AlertCircleIcon,
  CheckCircle2Icon,
  PauseCircleIcon,
} from 'lucide-react';

import type { TaskView } from '@/lib/api/generated/model';
import { Button } from '@/components/ui/button';
import {
  blockedAccountSentence,
  hostsFromCredentials,
} from '@/features/identity';
import { locateTask, type Board } from '@/features/tasks';

import type { RunSummary } from '../types';

/** Named one by one up to this many; the rest are counted. */
const NAMED_LIMIT = 3;

/**
 * The newest run's message, when that run broke or was refused — the words
 * the drawer's failure panel would show for the same task. `null` for a task
 * flagged for attention for any other reason, which the callout then names
 * without a message rather than inventing one.
 */
function latestError(task: TaskView): string | null {
  const latest = [...(task.executions ?? [])].sort((a, b) =>
    a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0,
  )[0];
  if (!latest || (latest.status !== 'failed' && latest.status !== 'blocked')) {
    return null;
  }
  // Blocked for want of its person's own account (spec 160): said from what the
  // run recorded and naming the person, as the drawer says it to anyone but them.
  if (latest.status === 'blocked' && latest.credential_source === 'required') {
    return blockedAccountSentence(
      hostsFromCredentials(latest.forge_credentials),
      latest.started_by?.display_name ?? 'its person',
    );
  }
  return latest.error_message?.trim() || null;
}

export interface RunStatusBannerProps {
  summary: RunSummary;
  tasks: TaskView[];
  board: Board;
  onOpenTask?: (taskId: string) => void;
}

/**
 * The one thing to act on, said above the board rather than found on it.
 *
 * - **Attention:** every created task flagged `needs_attention`, by name, with
 *   the step it is on and its newest run's error, each opening its drawer.
 * - **Review:** every created task waiting for review, by name and step, and
 *   who a gate waits on — the same routing the card names.
 * - **All done:** once every task is done, that it is, and where the parent
 *   goes next.
 *
 * - **Closed:** a finished run says what it came to and where the parent went,
 *   and nothing else.
 *
 * Nothing otherwise: a run that is simply in progress already says so in the
 * progress bar above, and a standing "all fine" banner is noise.
 */
export function RunStatusBanner({
  summary,
  tasks,
  board,
  onOpenTask,
}: RunStatusBannerProps) {
  // A finished run: what it came to and where the parent went. No action
  // callouts — the run is history now, and a task still flagged is acted on
  // from its own board, not as part of a run that is over.
  if (summary.closed) {
    const allDone = summary.total > 0 && summary.done === summary.total;
    return (
      <div
        role="status"
        className={`${
          allDone
            ? 'bg-board-state-done/12 text-board-state-done'
            : 'bg-muted text-muted-foreground'
        } flex items-start gap-2 rounded-lg px-3 py-2 text-sm`}
      >
        <CheckCircle2Icon
          aria-hidden="true"
          className="mt-0.5 size-4 shrink-0"
          strokeWidth={1.75}
        />
        <span>
          <b className="font-medium">
            {allDone
              ? 'All tasks done.'
              : `This run closed with ${String(summary.done)} of ${String(summary.total)} done.`}
          </b>{' '}
          {summary.parentStepNow
            ? `The parent task moved on to ${summary.parentStepNow}.`
            : 'The parent task has moved on.'}
        </span>
      </div>
    );
  }

  if (summary.total > 0 && summary.done === summary.total) {
    return (
      <div
        role="status"
        className="bg-board-state-done/12 text-board-state-done flex items-start gap-2 rounded-lg px-3 py-2 text-sm"
      >
        <CheckCircle2Icon
          aria-hidden="true"
          className="mt-0.5 size-4 shrink-0"
          strokeWidth={1.75}
        />
        <span>
          <b className="font-medium">All tasks done.</b>{' '}
          {summary.nextStep
            ? `The parent task continues to ${summary.nextStep.name}.`
            : 'The parent task continues to its next step.'}
        </span>
      </div>
    );
  }

  const open = tasks.filter(
    (task) => locateTask(board, task.id)?.step.systemRole !== 'done',
  );
  // Attention over review, as `RunBreakdown` and the service rank them: a
  // task carrying both is listed once, under the more urgent heading.
  const attention = open.filter((task) => task.needs_attention);
  const review = open.filter(
    (task) => task.needs_review && !task.needs_attention,
  );
  if (attention.length === 0 && review.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      {attention.length > 0 ? (
        <FlaggedGroup
          tone="attention"
          heading={
            attention.length === 1
              ? '1 task needs attention'
              : `${String(attention.length)} tasks need attention`
          }
          tasks={attention}
          board={board}
          onOpenTask={onOpenTask}
        />
      ) : null}
      {review.length > 0 ? (
        <FlaggedGroup
          tone="review"
          heading={reviewHeading(review, board)}
          tasks={review}
          board={board}
          onOpenTask={onOpenTask}
        />
      ) : null}
    </div>
  );
}

/**
 * "2 tasks wait for your review at Verif" — the step named in the heading
 * when every one of them stands on it, which is the usual case (a batch
 * reaches the same gate together), and left to each row otherwise.
 */
function reviewHeading(tasks: TaskView[], board: Board): string {
  const count =
    tasks.length === 1 ? '1 task waits' : `${String(tasks.length)} tasks wait`;
  const steps = new Set(
    tasks.map((task) => locateTask(board, task.id)?.stepLabel),
  );
  const [only] = steps;
  return steps.size === 1 && only
    ? `${count} for review at ${only}`
    : `${count} for review`;
}

const TONE = {
  attention: {
    label: 'Needs attention',
    box: 'bg-board-state-failed/12',
    heading: 'text-board-state-failed',
    Icon: AlertCircleIcon,
    action: 'Open',
  },
  review: {
    label: 'Needs review',
    box: 'bg-board-state-waiting/12',
    heading: 'text-board-state-waiting',
    Icon: PauseCircleIcon,
    action: 'Review',
  },
} as const;

function FlaggedGroup({
  tone,
  heading,
  tasks,
  board,
  onOpenTask,
}: {
  tone: keyof typeof TONE;
  heading: string;
  tasks: TaskView[];
  board: Board;
  onOpenTask?: (taskId: string) => void;
}) {
  const { label, box, heading: headingClass, Icon, action } = TONE[tone];
  const named = tasks.slice(0, NAMED_LIMIT);
  const rest = tasks.length - named.length;
  const [first] = tasks;

  return (
    <div
      role="region"
      aria-label={label}
      className={`${box} flex flex-col gap-1.5 rounded-lg px-3 py-2 text-sm`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className={`${headingClass} flex items-center gap-2 font-medium`}>
          <Icon
            aria-hidden="true"
            className="size-4 shrink-0"
            strokeWidth={1.75}
          />
          {heading}
        </span>
        {/*
          The one thing to do next, as a button rather than only as the task
          names below it: it opens the first task, whose drawer carries the
          gate's own controls.
        */}
        {first && onOpenTask ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              onOpenTask(first.id);
            }}
          >
            {action}
          </Button>
        ) : null}
      </div>
      <ul className="flex flex-col gap-1 pl-6">
        {named.map((task) => {
          const error = tone === 'attention' ? latestError(task) : null;
          const location = locateTask(board, task.id);
          const waitingOn =
            location?.step.kind === 'human_gate'
              ? (task.assignee?.display_name ?? 'Unassigned')
              : null;
          return (
            <li key={task.id} className="flex min-w-0 flex-col">
              <span className="text-foreground min-w-0 truncate">
                <button
                  type="button"
                  onClick={() => onOpenTask?.(task.id)}
                  className="focus-visible:ring-ring rounded-sm font-medium underline-offset-4 hover:underline focus-visible:ring-3 focus-visible:outline-none"
                >
                  {task.title}
                </button>
                {location ? (
                  <span className="text-muted-foreground">
                    {' '}
                    · {location.stepLabel}
                  </span>
                ) : null}
                {waitingOn ? (
                  <span className="text-muted-foreground"> · {waitingOn}</span>
                ) : null}
              </span>
              {error ? (
                <span className="text-muted-foreground line-clamp-2 text-xs">
                  {error}
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
      {rest > 0 ? (
        <span className="text-muted-foreground pl-6 text-xs">
          and {rest} more
        </span>
      ) : null}
    </div>
  );
}
